import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

interface VisitorFormData {
  visitorName: string;
  visitorCompany: string;
  visitorContact: string;
  visitorEmail: string;
  contactPerson: string;
  contactEmail: string;
  groupSize: number;
  isGroup: boolean;
  rememberMe?: boolean;
}

interface FormErrors {
  visitorName?: string;
  visitorCompany?: string;
  visitorContact?: string;
  visitorEmail?: string;
  contactPerson?: string;
  groupSize?: string;
  isGroup?: string;
  contactEmail?: string;
}

const VisitorForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<VisitorFormData>(() => {
    // First try to get data from QuickLookup
    const selectedVisitor = localStorage.getItem('selectedVisitor');
    if (selectedVisitor) {
      // Clear the selected visitor data immediately after using it
      localStorage.removeItem('selectedVisitor');
      const data = JSON.parse(selectedVisitor);
      return {
        ...data,
        contactPerson: '', // Always start with empty contact person
        contactEmail: '', // Always start with empty contact email
        groupSize: 1, // Reset to default group size
        isGroup: false // Reset to false since we're not pre-filling group size
      };
    }
    
    // If no selected visitor, check if we're coming from lookup page
    const fromLookup = sessionStorage.getItem('fromLookup');
    if (fromLookup === 'true') {
      // Clear the flag
      sessionStorage.removeItem('fromLookup');
      // Try to get saved data from localStorage
      const savedData = localStorage.getItem('visitorData');
      return savedData ? JSON.parse(savedData) : {
        visitorName: '',
        visitorCompany: '',
        visitorContact: '',
        visitorEmail: '',
        contactPerson: '',
        contactEmail: '',
        groupSize: 1,
        isGroup: false,
        rememberMe: false
      };
    }
    
    // Default empty form
    return {
      visitorName: '',
      visitorCompany: '',
      visitorContact: '',
      visitorEmail: '',
      contactPerson: '',
      contactEmail: '',
      groupSize: 1,
      isGroup: false,
      rememberMe: false
    };
  });
  const [contactPersons, setContactPersons] = useState<Array<{name: string, email: string}>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [suggestions, setSuggestions] = useState<Array<{name: string, email: string}>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Clear any stored visitor data when component mounts
  useEffect(() => {
    localStorage.removeItem('selectedVisitor');
  }, []);

  // Save form data to localStorage when it changes
  useEffect(() => {
    if (formData.rememberMe) {
      localStorage.setItem('visitorData', JSON.stringify(formData));
    } else {
      localStorage.removeItem('visitorData');
    }
  }, [formData]);

  useEffect(() => {
    // Load contact persons from S3 (http, not https)
    fetch('https://dev-redrocket-ai.s3.amazonaws.com/visitors-info/redrocketemployees.csv')
      .then(response => response.text())
      .then(data => {
        const contacts = data.split('\n')
          .slice(1) // Skip header
          .filter(line => line.trim()) // Filter out empty lines
          .map(line => {
            const [name, email] = line.split(',').map(item => item?.trim() || '');
            return { name, email };
          })
          .filter(person => person.name && person.email); // Filter out invalid entries
        setContactPersons(contacts);
      })
      .catch(error => {
        console.error('Error loading contact persons:', error);
        // Set some default contacts in case the CSV fails to load
        setContactPersons([
          { name: 'John Doe', email: 'john.doe@redrocket.energy' },
          { name: 'Jane Smith', email: 'jane.smith@redrocket.energy' }
        ]);
      });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validateForm = () => {
    const newErrors: FormErrors = {};
    
    if (!formData.visitorName.trim()) {
      newErrors.visitorName = 'Name is required';
    }
    
    if (!formData.visitorCompany.trim()) {
      newErrors.visitorCompany = 'Company is required';
    }
    
    if (!formData.visitorContact.trim()) {
      newErrors.visitorContact = 'Contact number is required';
    }
    
    if (!formData.visitorEmail.trim()) {
      newErrors.visitorEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.visitorEmail)) {
      newErrors.visitorEmail = 'Please enter a valid email address';
    }
    
    if (!formData.contactPerson) {
      newErrors.contactPerson = 'Please select a contact person';
    } else {
      const isValidContact = contactPersons.some(
        person => person.name.toLowerCase() === formData.contactPerson.toLowerCase()
      );
      if (!isValidContact) {
        newErrors.contactPerson = 'Please select a valid contact person from the list';
        setFormData(prev => ({ ...prev, contactPerson: '', contactEmail: '' }));
      }
    }

    if (!formData.contactEmail) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid contact email address';
    }
    
    if (formData.isGroup && formData.groupSize < 2) {
      newErrors.groupSize = 'Group size must be at least 2';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Rename function to be more appropriate
  const saveVisitorToS3 = async (visitorData: VisitorFormData) => {
    try {
      // First, try to fetch the current data from S3
      let data;
      try {
        const response = await fetch('https://1s916i26yc.execute-api.eu-west-1.amazonaws.com/api/get-visitors');
        data = await response.json();
        
        // Log the response to see its structure
        console.log('API response data:', data);
        
        // Check if data has the expected structure
        if (!data) {
          console.error('API returned null or undefined data');
          data = { visitors: [] };
        } else if (!data.visitors) {
          console.error('API response missing visitors array:', data);
          data.visitors = [];
        }
      } catch (error) {
        // If data doesn't exist, create a new data structure
        console.log('No existing visitors data found, creating new structure');
        data = {
          visitors: []
        };
      }
      
      // Ensure visitors is an array before using findIndex
      if (!Array.isArray(data.visitors)) {
        console.error('data.visitors is not an array:', data.visitors);
        data.visitors = [];
      }
      
      // Check if visitor already exists
      const existingIndex = data.visitors.findIndex((v: any) => v.visitorEmail === visitorData.visitorEmail);
      
      // Create a new visitor entry
      const newVisitor = {
        id: (data.visitors.length + 1).toString(),
        visitorName: visitorData.visitorName,
        visitorCompany: visitorData.visitorCompany,
        visitorContact: visitorData.visitorContact,
        visitorEmail: visitorData.visitorEmail,
        contactPerson: visitorData.contactPerson,
        groupSize: visitorData.groupSize,
        isGroup: visitorData.isGroup,
        timestamp: new Date().toISOString()
      };
      
      if (existingIndex >= 0) {
        // Update existing visitor
        data.visitors[existingIndex] = newVisitor;
      } else {
        // Add new visitor
        data.visitors.push(newVisitor);
      }
      
      // Save to S3 via API
      const saveResponse = await fetch('https://1s916i26yc.execute-api.eu-west-1.amazonaws.com/api/save-cached', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      // Check and print the response specifically for the /save-cached endpoint
      console.log('Save to S3 response status:', saveResponse.status);
      const responseData = await saveResponse.json();
      console.log('Save to S3 response data:', responseData);
      
      if (!saveResponse.ok) {
        throw new Error(`Failed to save to S3: ${saveResponse.statusText}`);
      }
      
      // Also save to localStorage for quick access
      localStorage.setItem('visitorsData', JSON.stringify(data));
      
      console.log('Visitor data saved to S3 successfully');
    } catch (error) {
      console.error('Error saving visitor data to S3:', error);
    }
  };

  // Update the handleSubmit function to use the new function name
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    
    // Validate form before proceeding
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }
    
    // Navigate to thank you page immediately
    navigate('/thankyou');
    
    // Handle form reset and API calls in the background
    try {
      // Clear stored data
      localStorage.removeItem('selectedVisitor');
      if (!formData.rememberMe) {
        localStorage.removeItem('visitorData');
      }
      
      // Reset form state
      setFormData({
        visitorName: '',
        visitorCompany: '',
        visitorContact: '',
        visitorEmail: '',
        contactPerson: '',
        contactEmail: '',
        groupSize: 1,
        isGroup: false,
        rememberMe: false
      });
      
      // If "Remember my details" is checked, save to S3
      if (formData.rememberMe) {
        await saveVisitorToS3(formData);
      }
      
      // Handle API calls in the background
      // Notify the contact
      axios.post(
        'https://1s916i26yc.execute-api.eu-west-1.amazonaws.com/api/notify-contact',
        {
          visitorName: formData.visitorName,
          company: formData.visitorCompany,
          visitorEmail: formData.visitorContact,
          contactPerson: formData.contactPerson,
          contactEmail: formData.contactEmail,
          groupSize: formData.groupSize,
          isGroup: formData.isGroup
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      ).then(response => {
        console.log('Notify contact response:', response.status);
      }).catch(error => {
        console.error('Error notifying contact:', error);
      });
      
      // Save visitor data
      axios.post(
        'https://1s916i26yc.execute-api.eu-west-1.amazonaws.com/api/save-visitor',
        {
          visitorName: formData.visitorName,
          visitorCompany: formData.visitorCompany,
          visitorContact: formData.visitorContact,
          visitorEmail: formData.visitorEmail,
          contactEmail: formData.contactEmail,
          contactPerson: formData.contactPerson,
          groupSize: formData.groupSize,
          isGroup: formData.isGroup
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      ).then(response => {
        console.log('Save visitor response:', response.status);
      }).catch(error => {
        console.error('Error saving visitor data:', error);
      });
    } catch (error) {
      console.error('Error in background API calls:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to submit form. Please try again.' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, contactPerson: value });
    
    if (value.trim()) {
      const filtered = contactPersons
        .filter(person => 
          person.name.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 2);
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (name: string) => {
    const selectedContact = contactPersons.find(person => person.name === name);
    setFormData({ 
      ...formData, 
      contactPerson: name,
      contactEmail: selectedContact?.email || ''
    });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div className="form-page-container">
      <div className="video-overlay"></div>
      <video className="background-video" autoPlay loop muted playsInline>
        <source
          src="https://redrocket.energy/wp-content/uploads/2024/05/RedRocketAnimation1200p_Loop.mp4"
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="form-container"
      >
        <div className="form-header">
          <motion.img 
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            src="https://redrocket.energy/wp-content/uploads/2021/03/rr-emblem.svg" 
            alt="Red Rocket Logo" 
            className="logo" 
          />
          <h1>Visitor Registration</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="visitorName">Visitor's Name</label>
            <input
              id="visitorName"
              type="text"
              value={formData.visitorName}
              onChange={(e) => setFormData({...formData, visitorName: e.target.value})}
              className={`form-input ${errors.visitorName ? 'error' : ''}`}
              placeholder="Enter full name"
            />
            {errors.visitorName && (
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="error-message"
              >
                {errors.visitorName}
              </motion.span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="visitorCompany">Company</label>
            <input
              id="visitorCompany"
              type="text"
              value={formData.visitorCompany}
              onChange={(e) => setFormData({...formData, visitorCompany: e.target.value})}
              className={`form-input ${errors.visitorCompany ? 'error' : ''}`}
              placeholder="Company name"
            />
            {errors.visitorCompany && (
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="error-message"
              >
                {errors.visitorCompany}
              </motion.span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="visitorContact">Contact Number</label>
            <input
              id="visitorContact"
              type="tel"
              value={formData.visitorContact}
              onChange={(e) => setFormData({...formData, visitorContact: e.target.value})}
              className={`form-input ${errors.visitorContact ? 'error' : ''}`}
              placeholder="Enter contact number"
            />
            {errors.visitorContact && (
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="error-message"
              >
                {errors.visitorContact}
              </motion.span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="visitorEmail">Email Address</label>
            <input
              id="visitorEmail"
              type="email"
              value={formData.visitorEmail}
              onChange={(e) => setFormData({...formData, visitorEmail: e.target.value})}
              className={`form-input ${errors.visitorEmail ? 'error' : ''}`}
              placeholder="Enter email address"
            />
            {errors.visitorEmail && (
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="error-message"
              >
                {errors.visitorEmail}
              </motion.span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="contactPerson">Red Rocket Contact</label>
            <div className="contact-input-container">
              <input
                id="contactPerson"
                type="text"
                value={formData.contactPerson}
                onChange={handleContactInput}
                className={`form-input ${errors.contactPerson ? 'error' : ''}`}
                placeholder="Start typing to search..."
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="suggestions-container" ref={suggestionsRef}>
                  {suggestions.map((person) => (
                    <div
                      key={person.email}
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(person.name)}
                    >
                      <span className="suggestion-name">{person.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {errors.contactPerson && (
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="error-message"
              >
                {errors.contactPerson}
              </motion.span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="contactEmail">Contact Email</label>
            <input
              id="contactEmail"
              type="email"
              value={formData.contactEmail}
              onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
              className={`form-input ${errors.contactEmail ? 'error' : ''}`}
              placeholder="Enter contact email address"
            />
            {errors.contactEmail && (
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="error-message"
              >
                {errors.contactEmail}
              </motion.span>
            )}
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isGroup}
                onChange={(e) => setFormData({...formData, isGroup: e.target.checked})}
                className="checkbox-input"
              />
              <span className="checkbox-text">This is a group visit</span>
            </label>
          </div>

          <AnimatePresence>
            {formData.isGroup && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="form-group"
              >
                <label htmlFor="groupSize">Number of Visitors</label>
                <input
                  id="groupSize"
                  type="number"
                  min="2"
                  value={formData.groupSize}
                  onChange={(e) => setFormData({...formData, groupSize: parseInt(e.target.value)})}
                  className={`form-input ${errors.groupSize ? 'error' : ''}`}
                />
                {errors.groupSize && (
                  <motion.span 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="error-message"
                  >
                    {errors.groupSize}
                  </motion.span>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                className="checkbox-input"
                checked={formData.rememberMe}
                onChange={(e) => setFormData({...formData, rememberMe: e.target.checked})}
              />
              <span className="checkbox-text">Remember my details</span>
            </label>
          </div>

          <motion.button 
            type="submit" 
            className={`submit-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? 'Submitting...' : 'Complete'}
          </motion.button>
        </form>
      </motion.div>

      <AnimatePresence>
        {showSuccess && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="success-overlay"
          >
            <div className="success-content">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="success-icon"
              >
                ✓
              </motion.div>
              <h2>Registration Successful!</h2>
              <p>Redirecting to thank you page...</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VisitorForm;